// Browsers ship no hyphenation patterns for Estonian, Latvian or Lithuanian,
// so `hyphens: auto` does nothing there, and a word wider than its card
// either spills out or, with overflow-wrap, drops its last letters onto a new
// line. <hyphenated-title> measures each word against its own width and
// hyphenates only the words that cannot fit; the rest keep wrapping whole.
//
// With the `shrink` attribute (section and page headings) the heading first
// shrinks its font size until its longest word fits, down to MIN_SCALE of its
// normal size, so a word is hyphenated only as a last resort.
//
// With `fit-box` as well, the heading keeps shrinking until the nearest
// [data-heading-box] ancestor stops scrolling. A slide's text sits in a box
// capped at the slide height, and a heading that wraps to three lines pushes
// the button below the box's edge, out of sight. The shared floor still holds:
// past it the heading stays at MIN_SCALE and the box scrolls.
//
// With the `hyphenate` attribute (the product page title) the heading keeps
// its size and words that fit get soft hyphens too, so a long word breaks at
// the end of a line the way body text does, instead of moving whole to the
// next line and leaving a gap. These breaks are optional, so only the safe
// ones are offered (see hyphenate() below).
if (!customElements.get('hyphenated-title')) {
  const SOFT_HYPHEN = '­';
  const VOWELS = 'aeiouyõäöüåæøáàâéèêíìîóòôúùûāēīōūąęėįųаеёиоуыэюяіє';
  // These never open a syllable (под-ъезд would be wrong).
  const NEVER_FIRST = 'ьъй';
  // Fewest letters left on the line before a break, and carried after it.
  const MIN_BEFORE = 2;
  const MIN_AFTER = 3;
  // The same for an optional break in a word that fits: no "Ad-" left at a
  // line end, no "der" carried after it.
  const SAFE_MIN_BEFORE = 3;
  const SAFE_MIN_AFTER = 4;
  // Onset clusters kept attached to the syllable they start, for languages
  // other than Estonian and Finnish (see hyphenate() below).
  const ONSETS = new Set([
    'pr', 'tr', 'kr', 'br', 'dr', 'gr', 'fr',
    'pl', 'kl', 'bl', 'gl', 'fl',
    'dz', 'dž', 'ch',
  ]);
  // s + stop onsets: kept attached only when a vowel precedes the s, so a
  // compound joint (Swedish genitive-s) isn't misread as an onset.
  const S_STOP_ONSETS = new Set(['sk', 'sp', 'st', 'str', 'spr', 'skr']);
  // Consonant pairs an optional break never splits: the onsets, whose split
  // is right in an Estonian word (komp-lekt) but wrong across a compound
  // joint (kätek-reem) or in an English name (Hyd-romax, Minec-raft), and
  // English digraphs (Supert-hings, Hed-gehogs). Outside Estonian and Finnish
  // the s + stop onsets and Swedish tv and kv are kept too (FÖRBES-TÄLL,
  // ultrat-vättbara).
  const SAFE_KEPT_PAIRS = new Set([...ONSETS, 'cr', 'cl', 'sh', 'th', 'ph', 'gh', 'ck', 'wh', 'dg']);
  const SAFE_KEPT_ONSETS = new Set([...S_STOP_ONSETS, 'tv', 'kv']);
  // Compound heads the syllable rule can't see. Advent calendars are about a
  // third of the catalogue, and every language spells the word as one
  // compound (advendikalender, joulukalenteri, adventskalender). A word with
  // one of these breaks at the joint and nowhere after it: Advendi-kalender,
  // never Advendika-lender.
  const COMPOUND_HEADS = ['kalend', 'kalent'];

  // Smallest share of its normal size a `shrink` heading is scaled down to,
  // for the word fit and the box fit alike.
  const MIN_SCALE = 0.7;

  const isVowel = (char) => char !== '' && VOWELS.includes(char.toLowerCase());
  const isMark = (char) => /\p{M}/u.test(char);

  // A break goes before the consonant that opens the next syllable. For
  // Estonian and Finnish that is simply the last consonant of a run a vowel
  // follows (loov-komp-lek-tid). Elsewhere it keeps listed onset clusters
  // together: a stop or f followed by r or l (Jul-klap-par), plus dz, dž
  // and ch. The clusters sk, sp, st, str, spr and skr stay together only
  // when a vowel precedes the s. A consonant before the s rules them out, giving
  // Ad-vents-ka-len-drar and Cons-truc-tors, where the shorter tr is used.
  // A cluster is used only if its start still leaves MIN_BEFORE letters
  // on the line; otherwise the shorter cluster is tried, and failing
  // that, the plain last-consonant break.
  //
  // With `safe`, for a word that fits, a break is kept only where every
  // language and reading agrees: a lone consonant between vowels
  // (Advendi-kalender) or two consonants that are not a kept pair
  // (Adven-di). A run of three or more is where compounds meet and prefixes
  // end (Kropps-skrubb, pār-steigums, бес-проводной), and the last-consonant
  // rule splits those in the wrong place, so such a run gets no break.
  const hyphenate = (word, lang, safe = false) => {
    const keepOnsets = lang !== 'et' && lang !== 'fi';
    const minBefore = safe ? SAFE_MIN_BEFORE : MIN_BEFORE;
    const minAfter = safe ? SAFE_MIN_AFTER : MIN_AFTER;
    const breaks = new Set();
    let seenVowel = isVowel(word[0]);
    for (let i = 1; i < word.length; i++) {
      const opensSyllable =
        !isVowel(word[i]) && !isMark(word[i]) && !NEVER_FIRST.includes(word[i].toLowerCase()) && isVowel(word[i + 1] || '');
      if (opensSyllable && seenVowel) {
        let breakAt = i;
        if (safe) {
          let start = i;
          while (start > 0 && !isVowel(word[start - 1]) && !isMark(word[start - 1])) start--;
          const run = word.slice(start, i + 1).toLowerCase();
          const kept = SAFE_KEPT_PAIRS.has(run) || (keepOnsets && SAFE_KEPT_ONSETS.has(run));
          if (run.length > 2 || kept) breakAt = -1;
        } else if (keepOnsets) {
          for (const len of [3, 2]) {
            const start = i - len + 1;
            if (start < MIN_BEFORE) continue;
            const cluster = word.slice(start, i + 1);
            if ([...cluster].some(isVowel)) continue;
            const lower = cluster.toLowerCase();
            if (S_STOP_ONSETS.has(lower)) {
              if (!isVowel(word[start - 1])) continue;
            } else if (!ONSETS.has(lower)) {
              continue;
            }
            breakAt = start;
            break;
          }
        }
        if (breakAt >= minBefore && word.length - breakAt >= minAfter) breaks.add(breakAt);
      }
      seenVowel = seenVowel || isVowel(word[i]);
    }
    const lowerWord = word.toLowerCase();
    const headAt = Math.max(...COMPOUND_HEADS.map((head) => lowerWord.lastIndexOf(head)));
    if (headAt > 0) {
      for (const at of breaks) if (at > headAt) breaks.delete(at);
      if (headAt >= minBefore) breaks.add(headAt);
    }
    let result = word[0];
    for (let i = 1; i < word.length; i++) {
      if (breaks.has(i)) result += SOFT_HYPHEN;
      result += word[i];
    }
    return result;
  };

  customElements.define('hyphenated-title', class HyphenatedTitle extends HTMLElement {
    connectedCallback() {
      this.fit();
      // The web font changes word widths without resizing the card.
      document.fonts.ready.then(() => this.fit());
      // Refit a frame later, so the new line count doesn't resize the element
      // inside its own observer callback.
      this.observer = new ResizeObserver(() => {
        if (this.clientWidth !== this.fittedWidth || this.boxHeights() !== this.fittedBoxHeights) {
          requestAnimationFrame(() => this.fit());
        }
      });
      this.observer.observe(this);
      // The box and its parent change height with the breakpoints. When the
      // parent grows, a box that fits exactly keeps its height, so only the
      // parent's change lets the heading grow back. The heading's own
      // size changes the box only until fit() records the result, so a refit
      // that ends where it began does not fire again.
      const box = this.box();
      if (box) {
        this.observer.observe(box);
        this.observer.observe(box.parentElement);
      }
    }

    disconnectedCallback() {
      this.observer.disconnect();
    }

    box() {
      return this.hasAttribute('fit-box') ? this.closest('[data-heading-box]') : null;
    }

    boxHeights() {
      const box = this.box();
      return box ? `${box.clientHeight}/${box.parentElement.clientHeight}` : '';
    }

    // The heading element around this wrapper carries the font size.
    setSize(size) {
      const heading = this.parentElement;
      heading.style.fontSize = `${size.toFixed(2)}px`;
      if (this.lineHeight) heading.style.lineHeight = `${(this.lineHeight * size / this.base).toFixed(2)}px`;
    }

    shrinkToFit(width) {
      const { base } = this;
      // nowrap keeps each word's range on one line, so it measures its full width.
      this.style.whiteSpace = 'nowrap';
      const range = document.createRange();
      const walker = document.createTreeWalker(this, NodeFilter.SHOW_TEXT);
      let widestWhole = 0;
      let widestPart = 0;
      for (let node = walker.nextNode(); node; node = walker.nextNode()) {
        // Drop soft hyphens left by an earlier fit, so offsets match the node.
        if (node.data.includes(SOFT_HYPHEN)) node.data = node.data.replaceAll(SOFT_HYPHEN, '');
        for (const [regex, key] of [[/\S+/g, 'whole'], [/[^\s-]+-?/g, 'part']]) {
          for (const { 0: word, index } of node.data.matchAll(regex)) {
            range.setStart(node, index);
            range.setEnd(node, index + word.length);
            const measured = range.getBoundingClientRect().width;
            if (key === 'whole') widestWhole = Math.max(widestWhole, measured);
            else widestPart = Math.max(widestPart, measured);
          }
        }
      }
      this.style.whiteSpace = '';
      if (!base) return;
      // A compound stays whole and shrinks like any word. Only when keeping it
      // whole needs more than the floor does the heading measure its parts, so
      // the compound breaks after its own hyphen at full size.
      const fitsWhole = base * (width - 2) / widestWhole >= base * MIN_SCALE;
      const widest = fitsWhole ? widestWhole : widestPart;
      if (widest <= width) return;
      this.setSize(Math.max(base * (width - 2) / widest, base * MIN_SCALE));
    }

    // How far the box's content reaches below its bottom edge, in fractional
    // pixels. scrollHeight and clientHeight are rounded to whole pixels, so
    // content that overflows by a fraction of one reads as fitting, yet the
    // browser still gives the box its scrollbar, which narrows the text and
    // wraps the heading onto another line, pushing the button out of sight.
    // A box sized to its content measures zero here, give or take the 1/64 px
    // that layout snaps an em or rem margin to, so the test below allows 0.05 px.
    contentBelow(box) {
      const boxStyle = getComputedStyle(box);
      const inner = box.getBoundingClientRect().bottom
        - parseFloat(boxStyle.borderBottomWidth) - parseFloat(boxStyle.paddingBottom);
      let bottom = -Infinity;
      for (const child of box.children) {
        const rect = child.getBoundingClientRect();
        if (!rect.height && !rect.width) continue;
        bottom = Math.max(bottom, rect.bottom + parseFloat(getComputedStyle(child).marginBottom));
      }
      return bottom === -Infinity ? 0 : bottom + box.scrollTop - inner;
    }

    // Runs after hyphenation, which decides the line count. Only a box that
    // overflows shrinks the heading, so one that fits keeps its size.
    shrinkToBox() {
      const box = this.box();
      if (!box || !box.clientHeight || !this.base) return;
      const overflows = () => box.scrollHeight > box.clientHeight || this.contentBelow(box) > 0.05;
      if (!overflows()) return;
      const heading = this.parentElement;
      let tooBig = parseFloat(getComputedStyle(heading).fontSize);
      let fits = this.base * MIN_SCALE;
      this.setSize(fits);
      if (overflows()) return;
      for (let i = 0; i < 8; i++) {
        const mid = (fits + tooBig) / 2;
        this.setSize(mid);
        if (overflows()) tooBig = mid;
        else fits = mid;
      }
      this.setSize(fits);
    }

    hyphenateWords(width) {
      const lang = (document.documentElement.lang || '').slice(0, 2).toLowerCase();
      const all = this.hasAttribute('hyphenate');
      const range = document.createRange();
      const walker = document.createTreeWalker(this, NodeFilter.SHOW_TEXT);
      for (let node = walker.nextNode(); node; node = walker.nextNode()) {
        const stripped = node.data.replaceAll(SOFT_HYPHEN, '').normalize('NFC');
        if (stripped !== node.data) node.data = stripped;
        const text = stripped;
        let fitted = '';
        let end = 0;
        for (const { 0: word, index } of text.matchAll(/[\p{L}\p{M}]+/gu)) {
          range.setStart(node, index);
          range.setEnd(node, index + word.length);
          const fits = range.getBoundingClientRect().width <= width;
          // A name with a capital inside (SquarePants, BabyBjörn) is broken
          // only when it cannot fit.
          if (fits && (!all || /\p{Ll}\p{Lu}/u.test(word))) continue;
          const hyphenated = hyphenate(word, lang, fits);
          if (hyphenated === word) continue;
          fitted += text.slice(end, index) + hyphenated;
          end = index + word.length;
        }
        if (end) node.data = fitted + text.slice(end);
      }
    }

    fit() {
      // A space-taking scrollbar would narrow the text during the probes and
      // leave a size that only fits without it, so measure without one.
      const box = this.box();
      if (box) box.style.overflowY = 'hidden';
      if (!this.clientWidth) {
        if (box) box.style.overflowY = '';
        return;
      }
      const style = getComputedStyle(this);
      const fullWidth = this.clientWidth;
      const width = fullWidth - parseFloat(style.paddingLeft) - parseFloat(style.paddingRight);
      if (this.hasAttribute('shrink') || this.hasAttribute('fit-box')) {
        const heading = this.parentElement;
        heading.style.fontSize = '';
        heading.style.lineHeight = '';
        const headingStyle = getComputedStyle(heading);
        this.base = parseFloat(headingStyle.fontSize);
        this.lineHeight = parseFloat(headingStyle.lineHeight);
      }
      if (this.hasAttribute('shrink')) this.shrinkToFit(width);
      this.hyphenateWords(width);
      this.shrinkToBox();
      if (box) box.style.overflowY = '';
      // A heading stuck at the floor keeps its scrollbar, so the text ends up
      // narrower than the width it was hyphenated for. Hyphenate again for it.
      const shown = this.clientWidth;
      if (shown < fullWidth) this.hyphenateWords(width - (fullWidth - shown));
      this.fittedWidth = this.clientWidth;
      this.fittedBoxHeights = this.boxHeights();
    }
  });
}
